import { Card, CardBody } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { FileText } from 'lucide-react';
import { formatDate, formatMoney } from '@/lib/format';
import { useServiceInvoices, type ServiceInvoice } from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;

/**
 * Receipt tab (read-only) — the invoices / receipts raised for this service.
 * Manual-invoice / statement generation actions are S3.
 */
export function ServiceReceiptTab({ id, currency = 'VND' }: { id: number; currency?: string }) {
  const invoices = useServiceInvoices(id);
  if (invoices.isError) return <TabError title="Failed to load receipts" onRetry={() => invoices.refetch()} />;
  if (invoices.isLoading) return <TabLoading rows={4} />;

  const money = (v?: number) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, currency)}</span>;

  const invoiceColumns: ColumnDef<ServiceInvoice, unknown>[] = [
    { id: 'invoice_id', header: 'ID', cell: ({ row }) => row.original.invoice_id },
    { id: 'invoice_type', header: 'Type', cell: ({ row }) => row.original.invoice_type || '—' },
    { id: 'invoice_status', header: 'Status', cell: ({ row }) => <StatusPill tone="neutral">{row.original.invoice_status || '—'}</StatusPill> },
    { id: 'invoice_amount', header: rightHeader('Amount'), cell: ({ row }) => money(row.original.invoice_amount) },
    { id: 'invoice_vat', header: rightHeader('VAT'), cell: ({ row }) => money(row.original.invoice_vat) },
    { id: 'invoice_total', header: rightHeader('Total'), cell: ({ row }) => money(row.original.invoice_total) },
    { id: 'period', header: 'Period', cell: ({ row }) => `${formatDate(row.original.invoice_apply_startdate)} → ${formatDate(row.original.invoice_apply_enddate)}` },
    { id: 'invoice_duedate', header: rightHeader('Due'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{formatDate(row.original.invoice_duedate)}</span> },
  ];

  const rows = invoices.data?.results ?? [];
  return (
    <Card flat>
      <CardBody>
        <DataTable
          columns={invoiceColumns}
          data={rows}
          getRowId={(r) => String(r.invoice_id)}
          emptyState={<EmptyState icon={<FileText size={24} />} title="No receipts" description="No invoices have been raised for this service yet." />}
        />
      </CardBody>
    </Card>
  );
}
